import {
  fetchFullyProjects,
  fetchCommonProjectsMilestones,
  fetchChangesProjectsMilestones,
} from '@gp/services/dpsReport/v2';

import type { IBaseResponse, IDateRange } from '@home/types/common';
import type { IDPSReportProjectSimpleDTO } from '@gp/types/dpsReport/common';
import type {
  ICommonProjectMilestoneDTOV2,
  ICalendarProjectMilestoneDTOV2,
  IChangesProjectMilestoneDTOV2,
} from '@gp/types/dpsReport/v2';

/**
 * 合并changes模块的projects下拉集合和miscellaneous模块的projects下拉集合
 * miscellaneous模块的下拉projects集合需要去重，过滤掉changes模块的projects下拉集合中相同的projects，并且数据改造为changes模块的projects拉下集合的类型
 * @param changesProjectsMilestones changes模块的projects下拉集合
 * @param miscellaneous miscellanesous模块的projects下拉集合
 * @returns 合并处理后的changes模块的projects下拉集合
 */
const mergeChangesMiscellaneousProjectsMilestones = (
  changesProjectsMilestones: IChangesProjectMilestoneDTOV2[] | null,
  miscellaneous: IDPSReportProjectSimpleDTO[] | null,
): IChangesProjectMilestoneDTOV2[] | null => {
  if (!Array.isArray(changesProjectsMilestones) && !Array.isArray(miscellaneous)) return [];
  if (!Array.isArray(miscellaneous)) return changesProjectsMilestones;

  const result: IChangesProjectMilestoneDTOV2[] = [...(changesProjectsMilestones || [])];
  // 重复校验的map，map中存在project_id的key，就代表project_id对应的projects已经存在了，就不需要再添加了
  const map: Record<string, true> = result.reduce((acc, next) => {
    const { project_id: projectId } = next || {};
    return { ...acc, [projectId]: true };
  }, {});

  miscellaneous.forEach((miscellaneousItem) => {
    const { project_id: projectId } = miscellaneousItem || {};
    if (!map[projectId]) {
      map[projectId] = true;
      result.push({ ...miscellaneousItem, cover: '', studio_id: '', studio: '', details: [] });
    }
  });

  return result;
};

/**
 * 由于后端接口分为hightlights、calendar一个接口，changes一个接口、其他接口， 把获取这几个模块的下拉projects选项数据分为了好几个接口
 * 所以加了这一层汇总的service层，页面只调用这一个service就可以获取到所有模块的下拉projects选项数据，并且调用后端几个接口是同时并发的，不存在接口阻塞问题，只是等所有接口都返回数据了，再返回给页面
 * 获取指定日期范围内的所有模块，下拉projects选项数据，及其关联的milestones数据（级联）
 * @param params 参数对象
 * @param params.start_date 开始日期
 * @param params.end_date 结束日期
 * @returns 所有模块，下拉projects选项数据，及其关联的milestones数据（级联）
 */
export const fetchProjectsMilestones = async (
  params: IDateRange,
): IBaseResponse<{
  hightlights: ICommonProjectMilestoneDTOV2[] | null;
  changes: IChangesProjectMilestoneDTOV2[] | null;
  miscellaneous: IDPSReportProjectSimpleDTO[] | null;
  calendar: ICalendarProjectMilestoneDTOV2[] | null;
}> => {
  const commonPromise = fetchCommonProjectsMilestones(params);
  const changesPromise = fetchChangesProjectsMilestones(params);
  const miscellaneousPromise = fetchFullyProjects();

  const res = await Promise.allSettled([commonPromise, changesPromise, miscellaneousPromise]);

  const [commonRes, changesRes, miscellaneousRes] = res || [];

  const { hightlights = [], calendar = [] } =
    commonRes?.status !== 'fulfilled' ? {} : (commonRes as PromiseFulfilledResult<any>)?.value?.data || {};

  const { changes = [] } =
    changesRes?.status !== 'fulfilled' ? {} : (changesRes as PromiseFulfilledResult<any>)?.value?.data || {};

  const { list = [] } =
    miscellaneousRes?.status !== 'fulfilled'
      ? {}
      : (miscellaneousRes as PromiseFulfilledResult<any>)?.value?.data || {};

  return {
    code: 0,
    msg: '',
    data: {
      hightlights: hightlights || [],
      changes: mergeChangesMiscellaneousProjectsMilestones(changes, list) || [],
      miscellaneous: list || [],
      calendar: calendar || [],
    },
    req: {},
  };
};
